import { z } from 'zod';

// Loose phone check — server does the authoritative E.164 normalization.
// We just reject obviously-too-short strings so UX doesn't let someone
// submit "abc" expecting it to work.
const phoneLike = z
  .string()
  .trim()
  .refine((v) => v === '' || /[0-9].*[0-9]/.test(v.replace(/\D/g, '').padEnd(0, '0') ? v : v), {
    message: 'Phone must include digits'
  })
  .refine((v) => v === '' || v.replace(/\D/g, '').length >= 10, {
    message: 'Phone must have at least 10 digits'
  });

export const customerFormSchema = z.object({
  displayName: z.string().trim().min(1, 'Required').max(200),
  email: z
    .string()
    .trim()
    .max(320)
    .refine((v) => v === '' || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v), {
      message: 'Must be a valid email'
    })
    .optional()
    .or(z.literal('')),
  phone: phoneLike.optional().or(z.literal('')),
  preferredDeliveryChannel: z.enum(['Email', 'Sms']),
  addressLine1: z.string().trim().max(200).optional().or(z.literal('')),
  addressLine2: z.string().trim().max(200).optional().or(z.literal('')),
  city: z.string().trim().max(100).optional().or(z.literal('')),
  region: z.string().trim().max(100).optional().or(z.literal('')),
  postalCode: z.string().trim().max(20).optional().or(z.literal('')),
  country: z.string().trim().max(100).optional().or(z.literal('')),
  notes: z.string().max(2000).optional().or(z.literal(''))
});

export type CustomerFormValues = z.infer<typeof customerFormSchema>;

/** Convert empty strings to null before sending to the API. */
export function toCustomerPayload(values: CustomerFormValues) {
  const blank = (v?: string | null) => (v == null || v.trim() === '' ? null : v);
  return {
    displayName: values.displayName.trim(),
    email: blank(values.email),
    phone: blank(values.phone),
    preferredDeliveryChannel: values.preferredDeliveryChannel,
    addressLine1: blank(values.addressLine1),
    addressLine2: blank(values.addressLine2),
    city: blank(values.city),
    region: blank(values.region),
    postalCode: blank(values.postalCode),
    country: blank(values.country),
    notes: blank(values.notes)
  };
}
